import { useEffect, useMemo, useState } from "react";
import { Loader2, Merge, Users, ChevronRight, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { api, formatApiError } from "../lib/api";
import { useApiBase } from "../context/ApiBaseContext";

/**
 * Iter-103 PART 2 + 3 + 4 — Character duplicate detection + manual merge.
 *
 * Two tabs:
 *   * "Possible duplicates" — server-computed groups of likely dupes
 *     ("DR CHONG" / "CHONG", "SARAH" / "SARAH B."). One-tap merge per
 *     group with a final-name picker.
 *   * "Manual merge" — owner selects 2+ characters from a searchable
 *     checklist, picks the primary + final display name, and merges.
 *
 * Both paths hit `POST /projects/{pid}/characters/merge`. Scenes,
 * notes, media, and continuity states follow the primary id
 * automatically.
 */
export default function CharacterMergeDialog({
    open,
    onOpenChange,
    projectId,
    characters,
    onMerged,
    presetPrimaryId = null,
    presetSecondaryIds = null,
    presetFinalName = "",
    presetMode = null,
}) {
    const apiBase = useApiBase();
    const [mode, setMode] = useState("auto"); // "auto" | "manual"
    const [dupeGroups, setDupeGroups] = useState([]);
    const [loadingGroups, setLoadingGroups] = useState(true);
    const [busyGroupKey, setBusyGroupKey] = useState(null);
    const [error, setError] = useState("");

    // Manual-merge state
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [primaryId, setPrimaryId] = useState("");
    const [finalName, setFinalName] = useState("");
    const [manualQuery, setManualQuery] = useState("");
    const [manualBusy, setManualBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        let aborted = false;
        (async () => {
            setLoadingGroups(true);
            setError("");
            try {
                const { data } = await api.get(
                    `${apiBase}/${projectId}/characters/duplicate-candidates`,
                );
                if (aborted) return;
                setDupeGroups(data.groups || []);
            } catch (e) {
                if (!aborted) setError(formatApiError(e));
            } finally {
                if (!aborted) setLoadingGroups(false);
            }
        })();
        return () => {
            aborted = true;
        };
    }, [open, projectId]);

    useEffect(() => {
        if (!open) {
            setMode("auto");
            setSelectedIds(new Set());
            setPrimaryId("");
            setFinalName("");
            setManualQuery("");
            setBusyGroupKey(null);
            setError("");
            return;
        }
        // Iter-132 — when the dialog is opened via the alias-suggestions
        // card we jump straight to the Manual tab pre-populated with
        // the pair the user picked. No auto-merge; the user still
        // has to hit Confirm.
        if (presetPrimaryId && presetSecondaryIds && presetSecondaryIds.length) {
            setMode(presetMode || "manual");
            setPrimaryId(presetPrimaryId);
            setSelectedIds(new Set([presetPrimaryId, ...presetSecondaryIds]));
            setFinalName(presetFinalName || "");
        }
    }, [open, presetPrimaryId, presetSecondaryIds, presetMode, presetFinalName]);

    const mergeGroup = async (group, primary, finalNameOverride) => {
        const key = group.map((c) => c.id).sort().join(":");
        setBusyGroupKey(key);
        try {
            const body = {
                primary_id: primary.id,
                secondary_ids: group
                    .filter((c) => c.id !== primary.id)
                    .map((c) => c.id),
                final_name: (finalNameOverride || primary.name || "").trim() || null,
            };
            const { data } = await api.post(
                `${apiBase}/${projectId}/characters/merge`,
                body,
            );
            toast.success(
                `Merged ${data.merged_count + 1} into ${data.final_name || data.primary?.name || primary.name}`,
            );
            // Remove the merged group locally.
            setDupeGroups((prev) => prev.filter((g) => g !== group));
            onMerged?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyGroupKey(null);
        }
    };

    const toggleSelect = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            if (next.size === 0) setPrimaryId("");
            else if (!next.has(primaryId)) setPrimaryId([...next][0]);
            return next;
        });
    };

    const runManualMerge = async () => {
        const ids = [...selectedIds];
        if (ids.length < 2 || !primaryId || !ids.includes(primaryId)) {
            toast.error("Select at least 2 characters and a primary.");
            return;
        }
        setManualBusy(true);
        try {
            const body = {
                primary_id: primaryId,
                secondary_ids: ids.filter((id) => id !== primaryId),
                final_name: finalName.trim() || null,
            };
            const { data } = await api.post(
                `${apiBase}/${projectId}/characters/merge`,
                body,
            );
            toast.success(
                `Merged ${data.merged_count + 1} into ${data.final_name || data.primary?.name}`,
            );
            onMerged?.();
            onOpenChange(false);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setManualBusy(false);
        }
    };

    const filteredChars = useMemo(() => {
        const q = manualQuery.trim().toLowerCase();
        const list = (characters || []).slice().sort((a, b) =>
            (a.name || "").localeCompare(b.name || "", undefined, {
                sensitivity: "base",
            }),
        );
        if (!q) return list;
        return list.filter((c) => (c.name || "").toLowerCase().includes(q));
    }, [characters, manualQuery]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="character-merge-dialog"
                className="max-w-lg bg-card border-white/10 max-h-[90vh] overflow-y-auto"
            >
                <DialogHeader className="pr-8">
                    <DialogTitle className="font-display text-xl font-bold text-white flex items-center gap-2">
                        <Merge size={18} /> Merge cast
                    </DialogTitle>
                    <DialogDescription className="text-neutral-400">
                        Clean up duplicate characters. Scenes, notes, images and fittings follow the primary character automatically.
                    </DialogDescription>
                </DialogHeader>

                <div className="mt-2 flex rounded-lg bg-secondary p-1">
                    <button
                        type="button"
                        data-testid="character-merge-mode-auto"
                        onClick={() => setMode("auto")}
                        className={[
                            "tap flex-1 h-9 rounded-md text-[11px] uppercase tracking-[0.2em] font-semibold transition-colors",
                            mode === "auto" ? "bg-primary text-primary-foreground" : "text-neutral-300 hover:text-white",
                        ].join(" ")}
                    >
                        Possible duplicates
                    </button>
                    <button
                        type="button"
                        data-testid="character-merge-mode-manual"
                        onClick={() => setMode("manual")}
                        className={[
                            "tap flex-1 h-9 rounded-md text-[11px] uppercase tracking-[0.2em] font-semibold transition-colors",
                            mode === "manual" ? "bg-primary text-primary-foreground" : "text-neutral-300 hover:text-white",
                        ].join(" ")}
                    >
                        Manual merge
                    </button>
                </div>

                {error ? (
                    <p
                        data-testid="character-merge-error"
                        className="mt-3 text-sm text-red-400 flex items-start gap-1.5"
                    >
                        <AlertCircle size={14} className="mt-0.5 shrink-0" />
                        {error}
                    </p>
                ) : null}

                {mode === "auto" ? (
                    <div className="mt-4 space-y-3" data-testid="character-merge-auto-panel">
                        {loadingGroups ? (
                            <p className="text-sm text-neutral-500 flex items-center gap-2">
                                <Loader2 size={14} className="animate-spin" /> Scanning cast…
                            </p>
                        ) : dupeGroups.length === 0 ? (
                            <div
                                data-testid="character-merge-auto-empty"
                                className="text-center py-6 text-sm text-neutral-400"
                            >
                                <Users size={24} className="mx-auto mb-2 text-neutral-600" />
                                No likely duplicates found. Your cast looks clean.
                            </div>
                        ) : (
                            dupeGroups.map((group) => (
                                <DuplicateGroupRow
                                    key={group.map((c) => c.id).join(":")}
                                    group={group}
                                    busy={busyGroupKey === group.map((c) => c.id).sort().join(":")}
                                    onMerge={mergeGroup}
                                />
                            ))
                        )}
                    </div>
                ) : (
                    <div className="mt-4 space-y-3" data-testid="character-merge-manual-panel">
                        <Input
                            data-testid="character-merge-search"
                            value={manualQuery}
                            onChange={(e) => setManualQuery(e.target.value)}
                            placeholder="Search characters"
                            className="bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                        />
                        <div className="rounded-lg border border-white/10 max-h-52 overflow-y-auto">
                            {filteredChars.length === 0 ? (
                                <p className="p-3 text-xs text-neutral-500">No characters match.</p>
                            ) : (
                                filteredChars.map((c) => {
                                    const checked = selectedIds.has(c.id);
                                    return (
                                        <label
                                            key={c.id}
                                            data-testid={`character-merge-pick-${c.id}`}
                                            className={[
                                                "flex items-center gap-3 p-3 cursor-pointer border-b border-white/5 last:border-0 transition-colors",
                                                checked ? "bg-primary/10" : "hover:bg-white/5",
                                            ].join(" ")}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => toggleSelect(c.id)}
                                                className="h-4 w-4 accent-primary"
                                            />
                                            <span className="flex-1 text-sm text-white truncate">
                                                {c.name}
                                            </span>
                                            {c.actor ? (
                                                <span className="text-xs text-neutral-500 truncate max-w-[40%]">
                                                    {c.actor}
                                                </span>
                                            ) : null}
                                        </label>
                                    );
                                })
                            )}
                        </div>

                        {selectedIds.size >= 2 ? (
                            <>
                                <div>
                                    <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                                        Keep as primary
                                    </Label>
                                    <select
                                        data-testid="character-merge-primary-select"
                                        value={primaryId}
                                        onChange={(e) => setPrimaryId(e.target.value)}
                                        className="mt-1.5 w-full h-10 px-3 rounded-md text-sm bg-secondary border-0 focus:ring-1 focus:ring-primary focus:outline-none"
                                    >
                                        {[...selectedIds].map((id) => {
                                            const c = characters.find((x) => x.id === id);
                                            return (
                                                <option key={id} value={id}>
                                                    {c?.name || id}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                                <div>
                                    <Label
                                        htmlFor="character-merge-final-name"
                                        className="text-[10px] uppercase tracking-[0.2em] text-neutral-500"
                                    >
                                        Final display name (optional)
                                    </Label>
                                    <Input
                                        id="character-merge-final-name"
                                        data-testid="character-merge-final-name"
                                        value={finalName}
                                        onChange={(e) => setFinalName(e.target.value)}
                                        placeholder={
                                            (characters.find((c) => c.id === primaryId)?.name || "") +
                                            "  (leave blank to keep primary name)"
                                        }
                                        maxLength={80}
                                        className="mt-1.5 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                                    />
                                </div>
                            </>
                        ) : (
                            <p className="text-[11px] text-neutral-500">
                                Select two or more characters to merge.
                            </p>
                        )}

                        <div className="flex gap-2 pt-2">
                            <Button
                                type="button"
                                variant="secondary"
                                data-testid="character-merge-manual-cancel"
                                onClick={() => onOpenChange(false)}
                                disabled={manualBusy}
                                className="flex-1 min-tap"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                data-testid="character-merge-manual-submit"
                                onClick={runManualMerge}
                                disabled={manualBusy || selectedIds.size < 2 || !primaryId}
                                className="flex-1 min-tap"
                            >
                                {manualBusy ? (
                                    <Loader2 size={14} className="animate-spin" />
                                ) : (
                                    `Merge ${selectedIds.size} into 1`
                                )}
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function DuplicateGroupRow({ group, busy, onMerge }) {
    const [primaryId, setPrimaryId] = useState(group[0]?.id || "");
    const [finalName, setFinalName] = useState(group[0]?.name || "");
    const primary = group.find((c) => c.id === primaryId) || group[0];
    useEffect(() => {
        setFinalName(primary?.name || "");
    }, [primaryId, primary?.name]);
    return (
        <div
            data-testid={`character-merge-group-${group.map((c) => c.id).sort().join("-")}`}
            className="rounded-lg border border-white/10 p-3 space-y-2"
        >
            <ul className="space-y-1">
                {group.map((c) => (
                    <li
                        key={c.id}
                        className="flex items-center justify-between text-sm text-white"
                    >
                        <span className="truncate">{c.name}</span>
                        <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 ml-2">
                            {c.scene_count ?? 0} scenes
                        </span>
                    </li>
                ))}
            </ul>
            <div className="grid grid-cols-1 gap-2">
                <div>
                    <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                        Keep as primary
                    </Label>
                    <select
                        value={primaryId}
                        onChange={(e) => setPrimaryId(e.target.value)}
                        data-testid={`character-merge-group-primary-${group[0]?.id}`}
                        className="mt-1 w-full h-9 px-2 rounded-md text-sm bg-secondary border-0 focus:ring-1 focus:ring-primary focus:outline-none"
                    >
                        {group.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                        Final display name
                    </Label>
                    <Input
                        value={finalName}
                        onChange={(e) => setFinalName(e.target.value)}
                        data-testid={`character-merge-group-final-${group[0]?.id}`}
                        maxLength={80}
                        className="mt-1 h-9 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                    />
                </div>
            </div>
            <Button
                type="button"
                data-testid={`character-merge-group-submit-${group[0]?.id}`}
                onClick={() => onMerge(group, primary, finalName)}
                disabled={busy}
                className="w-full min-tap h-9"
            >
                {busy ? (
                    <Loader2 size={13} className="animate-spin" />
                ) : (
                    <>
                        <ChevronRight size={13} className="mr-1" />
                        Merge {group.length} → 1
                    </>
                )}
            </Button>
        </div>
    );
}
